/**
 * useDeveloperOrg — client mirror of the developer-org gate (catalog v4).
 *
 * Answers ONE question: may the active workspace see developer-toolkit UI
 * (the Developer permission section, /admin/vms)? The answer comes from the
 * server (rbacGetDeveloperOrgStatus → rbac/developerOrgGate.ts) because only
 * the server can compose the pinned allowlist with the ops doc — no client
 * heuristic (uid lists, org names) is authoritative.
 *
 * FAILS CLOSED: any error, missing auth, or pending load resolves to
 * `isDeveloperOrg: false`. This hook is cosmetic by doctrine ("client hides
 * UI, server refuses data" — src/services/threadTags/featureFlags.ts): every
 * developer endpoint re-checks the gate server-side, so the worst a wrong
 * `false` does is hide chrome, and a wrong `true` shows chrome whose every
 * action 403s.
 *
 * Module-level cache (useRbac pattern): keyed uid::accountIdHint, 60s TTL,
 * shared in-flight promise so N mounts = 1 callable. Only clean results are
 * cached — errors are never seeded, so a transient failure retries on the
 * next mount.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getDeveloperOrgStatus } from '../services/rbacService';

export const DEVELOPER_ORG_CACHE_TTL_MS = 60_000;

interface CacheEntry {
  developerOrg: boolean;
  fetchedAtMs: number;
}

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<boolean>>();

/** Test seam. */
export function resetDeveloperOrgHookCacheForTests(): void {
  cache.clear();
  inflight.clear();
}

async function fetchDeveloperOrg(cacheKey: string, accountId?: string): Promise<boolean> {
  const existing = inflight.get(cacheKey);
  if (existing) return existing;
  const p = (async () => {
    try {
      const res = await getDeveloperOrgStatus(accountId);
      const value = res?.developerOrg === true;
      cache.set(cacheKey, { developerOrg: value, fetchedAtMs: Date.now() });
      return value;
    } finally {
      inflight.delete(cacheKey);
    }
  })();
  inflight.set(cacheKey, p);
  return p;
}

export interface UseDeveloperOrgResult {
  /** True ONLY when the server confirmed the active workspace is a developer org. */
  isDeveloperOrg: boolean;
  /** True while the first (uncached) answer is being fetched. */
  loading: boolean;
  refetch: () => void;
}

export function useDeveloperOrg(accountIdHint?: string): UseDeveloperOrgResult {
  const uid = getAuth().currentUser?.uid ?? '';
  const cacheKey = `${uid}::${accountIdHint ?? ''}`;

  const cached = cache.get(cacheKey);
  const cachedFresh =
    cached !== undefined && Date.now() - cached.fetchedAtMs < DEVELOPER_ORG_CACHE_TTL_MS;

  const [state, setState] = useState<{ value: boolean; loading: boolean }>(() =>
    cachedFresh
      ? { value: cached!.developerOrg, loading: false }
      : { value: false, loading: Boolean(uid) }
  );
  const mountedRef = useRef(true);

  const load = useCallback(() => {
    if (!uid) {
      setState({ value: false, loading: false });
      return;
    }
    const entry = cache.get(cacheKey);
    if (entry && Date.now() - entry.fetchedAtMs < DEVELOPER_ORG_CACHE_TTL_MS) {
      setState({ value: entry.developerOrg, loading: false });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    fetchDeveloperOrg(cacheKey, accountIdHint || undefined)
      .then((value) => {
        if (mountedRef.current) setState({ value, loading: false });
      })
      .catch(() => {
        // Fail closed — and do not cache the failure.
        if (mountedRef.current) setState({ value: false, loading: false });
      });
  }, [uid, cacheKey, accountIdHint]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  const refetch = useCallback(() => {
    cache.delete(cacheKey);
    load();
  }, [cacheKey, load]);

  return { isDeveloperOrg: state.value, loading: state.loading, refetch };
}
